import { supabase } from "./supabase";

// ─────────────────────────────────────
//  변환 함수
// ─────────────────────────────────────

// DB 컬럼명 → 앱 필드명
const toApp = (row) => ({
  id:              row.id,
  slug:            row.slug,
  name:            row.name,
  cat:             row.cat,
  price:           row.price,
  tag:             row.tag,
  sizes:           row.sizes || [],
  desc:            row.description,
  fabric:          row.fabric,
  origin:          row.origin,
  fit:             row.fit,
  naverUrl:        row.naver_url,
  naverUrlVisible: row.naver_url_visible ?? false,
  subcat:          row.subcat || "",
  imgs:            row.imgs || [],
  galleryImgs:     row.gallery_imgs || [],
  defaultColorIdx: row.default_color_idx ?? 0,
  fabricCare:      row.fabric_care || "",
  fitSizing:       row.fit_sizing || "",
  shippingReturns: row.shipping_returns || "",
  sizeGuideImg:    row.size_guide_img || null,
  discountRate:    row.discount_rate || 0,
  shippingFee:     row.shipping_fee ?? 0,
  colors: (row.product_colors || [])
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((c) => ({
      id:          c.id,
      name:        c.name,
      hex:         c.hex,
      imgs:        c.imgs || [],
      galleryImgs: c.gallery_imgs || [],
    })),
});

// 앱 필드명 → DB 컬럼명
const toDB = (form) => ({
  slug:              form.slug,
  name:              form.name,
  cat:               form.cat,
  price:             parseInt(form.price) || 0,
  tag:               form.tag || null,
  sizes:             form.sizes || [],
  description:       form.desc || "",
  fabric:            form.fabric || "",
  origin:            form.origin || "",
  fit:               form.fit || "",
  naver_url:         form.naverUrl || "",
  naver_url_visible: form.naverUrlVisible ?? false,
  subcat:            form.subcat || "",
  default_color_idx: form.defaultColorIdx ?? 0,
  fabric_care:       form.fabricCare || "",
  fit_sizing:        form.fitSizing || "",
  shipping_returns:  form.shippingReturns || "",
  discount_rate:     parseInt(form.discountRate) || 0,
  shipping_fee:      parseInt(form.shippingFee) || 0,
});

// ─────────────────────────────────────
//  slug 중복 방지: 충돌 시 -2, -3 ... suffix
// ─────────────────────────────────────
const ensureUniqueSlug = async (slug, excludeId = null) => {
  const base = slug || `product-${Date.now()}`;
  let candidate = base;
  let n = 2;
  for (let i = 0; i < 50; i++) {
    let q = supabase.from("products").select("id").eq("slug", candidate);
    if (excludeId) q = q.neq("id", excludeId);
    const { data } = await q.maybeSingle();
    if (!data) return candidate;
    candidate = `${base}-${n++}`;
  }
  return `${base}-${Date.now()}`;
};

// ─────────────────────────────────────
//  이미지: base64 → Storage 업로드
//  이미 URL이면 그대로 반환
// ─────────────────────────────────────
const uploadOrKeep = async (img, storagePath) => {
  if (!img) return null;
  if (!img.startsWith("data:")) return img; // 이미 Storage URL

  const res  = await fetch(img);
  const blob = await res.blob();
  const ext  = blob.type.split("/")[1]?.replace("jpeg", "jpg") || "jpg";
  const path = `${storagePath}_${Date.now()}.${ext}`;

  const { error } = await supabase.storage
    .from("product-images")
    .upload(path, blob, { upsert: true });
  if (error) throw error;

  const { data } = supabase.storage
    .from("product-images")
    .getPublicUrl(path);
  return data.publicUrl;
};

// ─────────────────────────────────────
//  제품 목록 조회
// ─────────────────────────────────────
export const fetchProducts = async () => {
  const { data, error } = await supabase
    .from("products")
    .select("*, product_colors(*)")
    .order("sort_order");
  if (error) throw error;
  return data.map(toApp);
};

// ─────────────────────────────────────
//  제품 저장 (추가 / 수정)
// ─────────────────────────────────────
export const saveProduct = async (form, view) => {
  const isNew = view === "add";
  let productId;

  if (isNew) {
    const slug = await ensureUniqueSlug(form.slug);
    const { data, error } = await supabase
      .from("products")
      .insert({ ...toDB(form), slug, imgs: [], gallery_imgs: [] })
      .select("id")
      .single();
    if (error) throw error;
    productId = data.id;
  } else {
    // DB에 실제로 존재하는지 확인 (fallback 기본 데이터는 DB에 없음)
    const { data: exists } = await supabase
      .from("products")
      .select("id")
      .eq("id", form.id)
      .maybeSingle();

    if (exists) {
      productId = form.id;
      const slug = await ensureUniqueSlug(form.slug, productId);
      const { error } = await supabase
        .from("products")
        .update({ ...toDB(form), slug })
        .eq("id", productId);
      if (error) throw error;
    } else {
      // DB에 없는 경우(기본 데이터 편집) → 새로 삽입
      const slug = await ensureUniqueSlug(form.slug);
      const { data, error } = await supabase
        .from("products")
        .insert({ ...toDB(form), slug, imgs: [], gallery_imgs: [] })
        .select("id")
        .single();
      if (error) throw error;
      productId = data.id;
    }
  }

  // 기본 이미지 업로드
  const imgs = (
    await Promise.all(
      (form.imgs || []).map((img, i) =>
        uploadOrKeep(img, `${productId}/main/${i}`)
      )
    )
  ).filter(Boolean);

  // 상세 이미지 업로드
  const galleryImgs = (
    await Promise.all(
      (form.galleryImgs || []).map((img, i) =>
        uploadOrKeep(img, `${productId}/gallery/${i}`)
      )
    )
  ).filter(Boolean);

  // 사이즈 가이드 이미지 업로드
  const sizeGuideImg = await uploadOrKeep(form.sizeGuideImg, `${productId}/size_guide`);

  // 이미지 URL 업데이트
  const { error: imgErr } = await supabase
    .from("products")
    .update({ imgs, gallery_imgs: galleryImgs, size_guide_img: sizeGuideImg || null })
    .eq("id", productId);
  if (imgErr) throw imgErr;

  // 기존 컬러 삭제 후 재삽입
  await supabase.from("product_colors").delete().eq("product_id", productId);

  if (form.colors?.length > 0) {
    const colorRows = await Promise.all(
      form.colors.map(async (col, i) => {
        const colorImgs = (
          await Promise.all(
            (col.imgs || []).map((img, j) =>
              uploadOrKeep(img, `${productId}/colors/${i}/main/${j}`)
            )
          )
        ).filter(Boolean);

        const colorGallery = (
          await Promise.all(
            (col.galleryImgs || []).map((img, j) =>
              uploadOrKeep(img, `${productId}/colors/${i}/gallery/${j}`)
            )
          )
        ).filter(Boolean);

        return {
          product_id:  productId,
          name:        col.name,
          hex:         col.hex,
          imgs:        colorImgs,
          gallery_imgs: colorGallery,
          sort_order:  i,
        };
      })
    );

    const { error: colErr } = await supabase
      .from("product_colors")
      .insert(colorRows);
    if (colErr) throw colErr;
  }

  return productId;
};

// ─────────────────────────────────────
//  히어로 슬라이드 설정
// ─────────────────────────────────────
const DEFAULT_SLIDES = [
  { id: 1, img: "/images/hero1.jpg", label: "26 S/S — TRAINING",    sub: "움직임을 위해 설계된 옷" },
  { id: 2, img: "/images/hero2.jpg", label: "26 S/S — PERFORMANCE", sub: "기능과 스타일의 균형" },
  { id: 3, img: "/images/hero3.jpg", label: "26 S/S — ESSENTIALS",  sub: "매일 입는 스포츠 유니폼" },
];

export const fetchSlides = async () => {
  try {
    const { data } = await supabase
      .from("settings")
      .select("value")
      .eq("key", "hero_slides")
      .maybeSingle();
    return data?.value?.length ? data.value : DEFAULT_SLIDES;
  } catch {
    return DEFAULT_SLIDES;
  }
};

export const saveSlides = async (slides) => {
  const processed = await Promise.all(
    slides.map(async (slide, i) => {
      const img = await uploadOrKeep(slide.img, `hero/slide_${i}`);
      return { ...slide, img: img || slide.img };
    })
  );
  const { error } = await supabase
    .from("settings")
    .upsert({ key: "hero_slides", value: processed });
  if (error) throw error;
  return processed;
};

// ─────────────────────────────────────
//  네비게이션 카테고리 설정
// ─────────────────────────────────────
const DEFAULT_NAV_CATS = [
  { title: "TOP",    catFilter: "Top",    items: [
    { label: "T-Shirts",    filter: "T-Shirts" },
    { label: "Hoodies",     filter: "Hoodies" },
    { label: "Base Layers", filter: "Base Layers" },
  ]},
  { title: "BOTTOM", catFilter: "Bottom", items: [
    { label: "Pants",  filter: "Pants" },
    { label: "Shorts", filter: "Shorts" },
  ]},
];

// 구버전 string[] 형식을 {label, filter}[] 로 마이그레이션
const migrateNavCats = (cats) =>
  cats.map(cat => ({
    ...cat,
    items: cat.items.map(item =>
      typeof item === "string" ? { label: item, filter: item } : item
    ),
  }));

export const fetchNavCats = async () => {
  try {
    const { data } = await supabase
      .from("settings")
      .select("value")
      .eq("key", "nav_cats")
      .maybeSingle();
    return migrateNavCats(data?.value || DEFAULT_NAV_CATS);
  } catch {
    return DEFAULT_NAV_CATS;
  }
};

export const saveNavCats = async (navCats) => {
  const { error } = await supabase
    .from("settings")
    .upsert({ key: "nav_cats", value: navCats });
  if (error) throw error;
};

// ─────────────────────────────────────
//  전체 메뉴 설정
// ─────────────────────────────────────
export const DEFAULT_MAIN_MENU = [
  { id: "shop",  label: "Shop",         type: "shop",  visible: true },
  { id: "new",   label: "New In",       type: "new",   visible: true },
  { id: "best",  label: "Best Sellers", type: "best",  visible: true },
  { id: "about", label: "About",        type: "about", visible: true },
  { id: "board", label: "고객센터",      type: "board", visible: true },
];

export const fetchMainMenu = async () => {
  try {
    const { data } = await supabase
      .from("settings")
      .select("value")
      .eq("key", "main_menu")
      .maybeSingle();
    return data?.value || DEFAULT_MAIN_MENU;
  } catch {
    return DEFAULT_MAIN_MENU;
  }
};

export const saveMainMenu = async (menu) => {
  const { error } = await supabase
    .from("settings")
    .upsert({ key: "main_menu", value: menu });
  if (error) throw error;
};

// ─────────────────────────────────────
//  브랜드 / 어바웃 이미지 설정
// ─────────────────────────────────────
const DEFAULT_BRAND_IMAGES = {
  brandStrip: null,
  aboutHero: null,
  collections: [
    { img: null, label: "TRAINING" },
    { img: null, label: "PERFORMANCE" },
    { img: null, label: "ESSENTIALS" },
  ],
};

export const fetchBrandImages = async () => {
  try {
    const { data } = await supabase
      .from("settings")
      .select("value")
      .eq("key", "brand_images")
      .maybeSingle();
    const raw = data?.value || DEFAULT_BRAND_IMAGES;
    return {
      ...raw,
      collections: (raw.collections || []).map((item, i) =>
        (item === null || typeof item === "string")
          ? { img: item, label: DEFAULT_BRAND_IMAGES.collections[i]?.label || "" }
          : item
      ),
    };
  } catch {
    return DEFAULT_BRAND_IMAGES;
  }
};

export const saveBrandImages = async (brandImages) => {
  const brandStrip = await uploadOrKeep(brandImages.brandStrip, "brand/strip");
  const aboutHero  = await uploadOrKeep(brandImages.aboutHero,  "brand/about_hero");
  const collections = await Promise.all(
    (brandImages.collections || DEFAULT_BRAND_IMAGES.collections).map(async (item, i) => {
      const img = await uploadOrKeep(item?.img ?? null, `brand/collection_${i}`);
      return { img: img ?? item?.img ?? null, label: item?.label || "" };
    })
  );
  const processed = {
    brandStrip:  brandStrip  ?? brandImages.brandStrip,
    aboutHero:   aboutHero   ?? brandImages.aboutHero,
    collections,
  };
  const { error } = await supabase
    .from("settings")
    .upsert({ key: "brand_images", value: processed });
  if (error) throw error;
  return processed;
};

// ─────────────────────────────────────
//  사이트 설정 (히어로 배너 텍스트 + 이벤트 배너)
// ─────────────────────────────────────
export const DEFAULT_SITE_SETTINGS = {
  heroBannerLeft:   '26 S/S — NOW AVAILABLE',
  heroBannerCenter: 'FREE SHIPPING OVER ₩100,000',
  heroBannerRight:  'UNDERLOOKED ✦ SEOUL',
  eventBanner: {
    enabled:   false,
    text:      '회원가입 시 ₩3,000 쿠폰 지급',
    bgColor:   '#1A1A1A',
    textColor: '#F2F1ED',
    link:      '',
  },
  couponBanner: {
    enabled:      false,
    image:        null,
    title:        '신규 회원 혜택',
    description:  '지금 가입하고 첫 주문 시 사용하세요',
    discountText: '₩3,000 할인',
    ctaText:      '쿠폰 받기',
    code:         'WELCOME3000',
    delaySeconds: 3,
    bgColor:      '#1A1A1A',
    textColor:    '#F2F1ED',
    accentColor:  '#C97A3D',
  },
  socialLinks: {
    instagram: '',
    tiktok:    '',
    kakao:     '',
  },
  footerLinks: {
    shop: [
      { label: "New Arrivals", action: "shop_new",    visible: true },
      { label: "Top",          action: "shop_Top",    visible: true },
      { label: "Bottom",       action: "shop_Bottom", visible: true },
      { label: "Best Sellers", action: "shop_best",   visible: true },
    ],
    info: [
      { label: "About",      action: "about",           visible: true },
      { label: "Stockist",   action: "info_stockist",   visible: true },
      { label: "Recruit",    action: "info_recruit",    visible: true },
      { label: "Membership", action: "info_membership", visible: true },
    ],
    help: [
      { label: "Contact",  action: "info_contact",  visible: true },
      { label: "FAQ",      action: "info_faq",      visible: true },
      { label: "Returns",  action: "info_returns",  visible: true },
      { label: "Shipping", action: "info_shipping", visible: true },
    ],
  },
  paymentMethods: {
    bank:     true,
    card:     false,
    naverPay: false,
    kakaoPay: false,
  },
};

export const fetchSiteSettings = async () => {
  try {
    const { data } = await supabase.from('settings').select('value').eq('key', 'site_settings').maybeSingle();
    return data?.value ? { ...DEFAULT_SITE_SETTINGS, ...data.value } : DEFAULT_SITE_SETTINGS;
  } catch {
    return DEFAULT_SITE_SETTINGS;
  }
};

export const saveSiteSettings = async (settings) => {
  const processed = { ...settings };
  if (processed.couponBanner?.image?.startsWith('data:')) {
    const url = await uploadOrKeep(processed.couponBanner.image, 'coupon_banner/image');
    processed.couponBanner = { ...processed.couponBanner, image: url };
  }
  const { error } = await supabase.from('settings').upsert({ key: 'site_settings', value: processed });
  if (error) throw error;
  return processed;
};

// ─────────────────────────────────────
//  제품 삭제
// ─────────────────────────────────────
export const deleteProduct = async (id) => {
  // product_colors는 CASCADE로 자동 삭제
  const { error } = await supabase
    .from("products")
    .delete()
    .eq("id", id);
  if (error) throw error;

  // Storage 이미지도 삭제 (폴더 단위)
  const { data: files } = await supabase.storage
    .from("product-images")
    .list(`${id}`, { limit: 200 });
  if (files?.length) {
    const paths = files.map((f) => `${id}/${f.name}`);
    await supabase.storage.from("product-images").remove(paths);
  }
};

// ─────────────────────────────────────
//  제품 노출 순서 저장
// ─────────────────────────────────────
export const saveProductOrder = async (orderedIds) => {
  await Promise.all(
    orderedIds.map((id, index) =>
      supabase.from("products").update({ sort_order: index }).eq("id", id)
    )
  );
};

// ═════════════════════════════════════
//  쿠폰
// ═════════════════════════════════════

export const fetchCoupons = async () => {
  const { data, error } = await supabase
    .from("coupons")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data;
};

export const saveCoupon = async (form) => {
  const payload = {
    code:                  form.code?.trim() || null,
    title:                 form.title,
    type:                  form.type,
    value:                 parseInt(form.value) || 0,
    max_discount:          form.maxDiscount ? parseInt(form.maxDiscount) : null,
    min_order_amount:      parseInt(form.minOrderAmount) || 0,
    usage_limit_per_user:  parseInt(form.usageLimitPerUser) || 1,
    starts_at:             form.startsAt || null,
    expires_at:            form.expiresAt || null,
    is_welcome:            !!form.isWelcome,
    welcome_expires_days:  form.isWelcome && form.welcomeExpiresDays ? parseInt(form.welcomeExpiresDays) : null,
    active:                !!form.active,
  };
  if (form.id) {
    const { error } = await supabase.from("coupons").update(payload).eq("id", form.id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from("coupons").insert(payload);
    if (error) throw error;
  }
};

export const deleteCoupon = async (id) => {
  const { error } = await supabase.from("coupons").delete().eq("id", id);
  if (error) throw error;
};

// 회원 검색 (쿠폰 대상 지정 발급용)
export const searchProfiles = async (query) => {
  if (!query?.trim()) return [];
  const q = query.trim();
  const { data, error } = await supabase
    .from("profiles")
    .select("id, username, name, email")
    .or(`username.ilike.%${q}%,name.ilike.%${q}%,email.ilike.%${q}%`)
    .limit(20);
  if (error) throw error;
  return data;
};

export const issueCouponToUsers = async (couponId, userIds) => {
  if (!userIds.length) return;
  const rows = userIds.map((userId) => ({ coupon_id: couponId, user_id: userId }));
  const { error } = await supabase.from("user_coupons").insert(rows);
  if (error) throw error;
};

// 내 쿠폰 목록 (마이페이지 / 체크아웃)
export const fetchMyCoupons = async (userId) => {
  const { data, error } = await supabase
    .from("user_coupons")
    .select("*, coupons(*)")
    .eq("user_id", userId)
    .order("issued_at", { ascending: false });
  if (error) throw error;
  return data;
};

export const redeemCoupon = async (userCouponId, orderId) => {
  const { data, error } = await supabase.rpc("redeem_coupon", {
    p_user_coupon_id: userCouponId,
    p_order_id: orderId,
  });
  if (error) throw error;
  return data;
};

// ═════════════════════════════════════
//  리뷰
// ═════════════════════════════════════

const REVIEW_SORTS = {
  latest:  { column: "created_at", ascending: false },
  highest: { column: "rating",     ascending: false },
  lowest:  { column: "rating",     ascending: true  },
  helpful: { column: "helpful_count", ascending: false },
};

export const fetchReviewStats = async (productId) => {
  const { data, error } = await supabase
    .rpc("get_review_stats", { p_product_id: productId })
    .maybeSingle();
  if (error) throw error;
  return data || { avg_rating: 0, total_count: 0, r5: 0, r4: 0, r3: 0, r2: 0, r1: 0 };
};

export const fetchReviews = async (productId, { sort = "latest", page = 0, pageSize = 5 } = {}) => {
  const { column, ascending } = REVIEW_SORTS[sort] || REVIEW_SORTS.latest;
  const from = page * pageSize;
  const to = from + pageSize - 1;
  const { data, error, count } = await supabase
    .from("reviews")
    .select("*", { count: "exact" })
    .eq("product_id", productId)
    .order("is_best", { ascending: false })   // 베스트 리뷰 항상 상단
    .order(column, { ascending })
    .range(from, to);
  if (error) throw new Error(error.message || "리뷰 조회 ���패");

  // 작성자 프로필 batch 조회
  const userIds = [...new Set((data || []).map(r => r.user_id).filter(Boolean))];
  let profileMap = {};
  if (userIds.length) {
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, name, username")
      .in("id", userIds);
    (profiles || []).forEach(p => { profileMap[p.id] = p; });
  }

  const reviews = (data || []).map(r => ({ ...r, profiles: profileMap[r.user_id] || null }));
  return { reviews, total: count ?? reviews.length };
};

export const fetchMyHelpfulVotes = async (userId, reviewIds) => {
  if (!userId || !reviewIds.length) return [];
  const { data, error } = await supabase
    .from("review_helpful_votes")
    .select("review_id")
    .eq("user_id", userId)
    .in("review_id", reviewIds);
  if (error) throw error;
  return data.map((r) => r.review_id);
};

export const checkVerifiedPurchase = async (userId, productId) => {
  if (!userId) return false;
  const { data, error } = await supabase
    .from("orders")
    .select("items")
    .eq("user_id", userId)
    .eq("status", "delivered");
  if (error) return false;
  return (data || []).some((o) => (o.items || []).some((it) => it.product_id === productId || it.productId === productId));
};

export const createReview = async ({ productId, userId, rating, title, content, images, reviewType = 'text' }) => {
  const verified = await checkVerifiedPurchase(userId, productId);

  const uploadedImages = (
    await Promise.all(
      (images || []).map(async (img, i) => {
        if (!img) return null;
        if (!img.startsWith("data:")) return img;
        const res  = await fetch(img);
        const blob = await res.blob();
        const ext  = blob.type.split("/")[1]?.replace("jpeg", "jpg") || "jpg";
        const path = `${productId}/${userId}_${Date.now()}_${i}.${ext}`;
        const { error } = await supabase.storage.from("review-images").upload(path, blob, { upsert: true });
        if (error) throw error;
        const { data } = supabase.storage.from("review-images").getPublicUrl(path);
        return data.publicUrl;
      })
    )
  ).filter(Boolean);

  const { error } = await supabase.from("reviews").insert({
    product_id: productId,
    user_id: userId,
    rating,
    title: title || "",
    content,
    images: uploadedImages,
    verified_purchase: verified,
    review_type: reviewType,
  });
  if (error) throw error;
};

export const toggleHelpful = async (reviewId, userId, currentlyVoted) => {
  if (currentlyVoted) {
    const { error } = await supabase
      .from("review_helpful_votes")
      .delete()
      .eq("review_id", reviewId)
      .eq("user_id", userId);
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from("review_helpful_votes")
      .insert({ review_id: reviewId, user_id: userId });
    if (error) throw error;
  }
};

export const deleteReview = async (id) => {
  const { error } = await supabase.from("reviews").delete().eq("id", id);
  if (error) throw error;
};

export const checkHasReviewed = async (userId, productId) => {
  if (!userId) return false;
  const { data } = await supabase
    .from("reviews")
    .select("id")
    .eq("user_id", userId)
    .eq("product_id", productId)
    .maybeSingle();
  return !!data;
};

export const grantPhotoReviewPoints = async (reviewId) => {
  const { error } = await supabase.rpc("grant_photo_review_points", { p_review_id: reviewId });
  if (error) throw error;
};

// ─────────────────────────────────────
//  적립금 현황 (관리자)
// ─────────────────────────────────────
const POINT_TYPE_LABELS = {
  earn_review: '리뷰 적립',
  earn_best:   'BEST 리뷰',
  spend_order: '주문 사용',
  expire:      '소멸',
  admin:       '관리자 지급',
};

export const fetchAllPointTransactions = async ({ page = 0, pageSize = 30, type = null } = {}) => {
  let q = supabase
    .from('point_transactions')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(page * pageSize, (page + 1) * pageSize - 1);
  if (type) q = q.eq('type', type);
  const { data, error, count } = await q;
  if (error) throw error;

  const userIds = [...new Set((data || []).map(t => t.user_id).filter(Boolean))];
  let profileMap = {};
  if (userIds.length) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, name, username')
      .in('id', userIds);
    (profiles || []).forEach(p => { profileMap[p.id] = p; });
  }

  return {
    transactions: (data || []).map(t => ({
      ...t,
      profiles: profileMap[t.user_id] || null,
      typeLabel: POINT_TYPE_LABELS[t.type] || t.type,
    })),
    total: count ?? 0,
  };
};

export const fetchPointSummary = async () => {
  const { data, error } = await supabase
    .from('user_points')
    .select('total_earned, total_used, available');
  if (error) throw error;
  return (data || []).reduce(
    (acc, r) => ({
      totalEarned: acc.totalEarned + (r.total_earned || 0),
      totalUsed:   acc.totalUsed   + (r.total_used   || 0),
      available:   acc.available   + (r.available    || 0),
    }),
    { totalEarned: 0, totalUsed: 0, available: 0 }
  );
};

// ─────────────────────────────────────
//  Info 페이지 콘텐츠
// ─────────────────────────────────────
export const DEFAULT_INFO_CONTENT = {
  contact: {
    title: "Contact", sub: "문의하기",
    sections: [
      { heading: "이메일", body: "hello@underlooked.xyz\n24시간 내 회신 드립니다." },
      { heading: "인스타그램", body: "@underlooked.official" },
      { heading: "카카오톡 채널", body: "UNDERLOOKED\n평일 10:00 – 18:00 운영" },
      { heading: "브랜드 위치", body: "Seoul, Korea\n(상세 주소는 이메일로 문의 바랍니다.)" },
    ],
  },
  faq: {
    title: "FAQ", sub: "자주 묻는 질문",
    sections: [
      { heading: "사이즈는 어떻게 선택하나요?", body: "각 제품 페이지의 사이즈 가이드를 참고해 주세요. 넉넉한 핏을 원하시면 평소보다 한 사이즈 크게 구매하시길 권장합니다." },
      { heading: "세탁은 어떻게 하나요?", body: "30°C 이하 찬물 세탁을 권장합니다. 표백제 및 드라이클리닝은 사용하지 마세요. 제품 안쪽 케어 라벨을 꼭 확인해 주세요." },
      { heading: "교환 / 반품이 가능한가요?", body: "수령 후 7일 이내, 태그 부착 및 미착용 상태의 제품에 한해 교환 / 반품이 가능합니다." },
      { heading: "재입고 알림을 받을 수 있나요?", body: "인스타그램 팔로우 또는 이메일 문의 시 재입고 알림을 받으실 수 있습니다." },
    ],
  },
  returns: {
    title: "Returns", sub: "교환 및 반품",
    sections: [
      { heading: "반품 신청 기간", body: "상품 수령 후 7일 이내 신청 가능합니다." },
      { heading: "반품 조건", body: "미착용 상태, 태그 및 포장 상태 유지, 세탁 또는 사용 흔적 없는 제품에 한합니다." },
      { heading: "교환 / 반품 신청 방법", body: "hello@underlooked.xyz 으로 주문번호와 사유를 기재하여 문의해 주세요." },
      { heading: "반품 불가 상품", body: "착용 또는 세탁된 제품\n태그 제거된 제품\n수령 후 7일 초과 제품\n기획전 / 세일 상품" },
      { heading: "교환 / 반품 배송비", body: "제품 불량 또는 오배송의 경우 당사 부담\n단순 변심의 경우 고객 부담 (₩3,000)" },
    ],
  },
  shipping: {
    title: "Shipping", sub: "배송 안내",
    sections: [
      { heading: "국내 배송비", body: "기본 배송비: ₩3,000\n₩100,000 이상 구매 시 무료 배송" },
      { heading: "배송 기간", body: "결제 완료 후 2–3 영업일 이내 출고\n(주말 · 공휴일 제외)" },
      { heading: "배송사", body: "CJ대한통운 / 로젠택배" },
      { heading: "해외 배송", body: "현재 해외 배송은 지원하지 않습니다." },
    ],
  },
  stockist: {
    title: "Stockist", sub: "판매처 안내",
    sections: [
      { heading: "온라인 공식 스토어", body: "네이버 스마트스토어\nsmartstore.naver.com/underlooked" },
      { heading: "오프라인", body: "현재 오프라인 매장은 운영하지 않습니다.\n팝업 스토어 정보는 인스타그램을 통해 공지됩니다." },
    ],
  },
  recruit: {
    title: "Recruit", sub: "채용 안내",
    sections: [
      { heading: "현재 채용 포지션", body: "현재 공개 채용 중인 포지션이 없습니다." },
      { heading: "자유 지원", body: "디자인, 마케팅, 운영 등 모든 분야에서 함께할 분을 환영합니다.\nhello@underlooked.xyz 으로 포트폴리오를 보내주세요." },
    ],
  },
  membership: {
    title: "Membership", sub: "멤버십",
    sections: [
      { heading: "UNDERLOOKED 멤버십", body: "멤버십 프로그램을 준비 중입니다.\n인스타그램 팔로우를 통해 출시 소식을 먼저 받아보세요." },
      { heading: "멤버십 혜택 (예정)", body: "신제품 사전 구매 기회\n시즌 특별 할인 쿠폰\n멤버 전용 이벤트 초대" },
    ],
  },
  terms: {
    title: "Terms", sub: "이용약관",
    sections: [
      { heading: "제1조 (목적)", body: "본 약관은 UNDERLOOKED(이하 \"회사\")가 운영하는 온라인 쇼핑몰에서 제공하는 서비스의 이용과 관련하여 회사와 회원의 권리, 의무 및 책임사항을 규정함을 목적으로 합니다." },
      { heading: "제2조 (정의)", body: "\"몰\"이란 회사가 상품 또는 서비스를 회원에게 제공하기 위해 운영하는 온라인 사이트를 의미합니다.\n\"회원\"이란 본 약관에 동의하고 회원가입을 완료한 자를 의미합니다.\n\"비회원\"이란 회원가입 없이 서비스를 이용하는 자를 의미합니다." },
      { heading: "제3조 (약관의 효력 및 변경)", body: "본 약관은 회원이 동의함으로써 효력이 발생합니다.\n회사는 관련 법령을 위반하지 않는 범위에서 약관을 변경할 수 있습니다.\n변경된 약관은 사이트 공지사항을 통해 공지하며, 공지 후에도 서비스를 계속 이용하는 경우 변경에 동의한 것으로 간주합니다." },
      { heading: "제4조 (회원가입)", body: "이용자는 회사가 정한 양식에 따라 회원정보를 입력하고 약관에 동의함으로써 회원가입을 신청합니다.\n회사는 다음 각 호에 해당하는 경우 회원가입을 제한할 수 있습니다.\n- 타인의 정보를 도용한 경우\n- 허위 정보를 기재한 경우\n- 기타 회원가입이 부적절하다고 판단되는 경우" },
      { heading: "제5조 (회원의 의무)", body: "회원은 다음 행위를 하여서는 안 됩니다.\n- 허위 정보 등록\n- 타인의 개인정보 도용\n- 서비스 운영 방해 행위\n- 법령 또는 공서양속에 반하는 행위" },
      { heading: "제6조 (서비스 제공)", body: "회사는 다음 서비스를 제공합니다.\n- 상품 정보 제공\n- 상품 구매 및 결제 서비스\n- 배송 서비스\n- 고객 상담 서비스\n- 기타 회사가 정하는 서비스" },
      { heading: "제7조 (회원 탈퇴 및 자격 상실)", body: "회원은 언제든지 탈퇴를 요청할 수 있습니다.\n회원이 약관을 위반한 경우 회사는 회원자격을 제한 또는 상실시킬 수 있습니다." },
      { heading: "제8조 (면책조항)", body: "회사는 천재지변 등 불가항력적인 사유로 서비스를 제공할 수 없는 경우 책임을 지지 않습니다.\n회사는 회원의 귀책사유로 인한 서비스 이용 장애에 대해 책임을 지지 않습니다." },
      { heading: "제9조 (준거법 및 관할법원)", body: "본 약관은 대한민국 법률에 따라 해석되며, 서비스 이용과 관련한 분쟁은 회사 소재지 관할 법원을 전속 관할 법원으로 합니다.\n\n※ 본 이용약관에 동의하지 않을 경우 회원가입이 제한될 수 있습니다." },
    ],
  },
  privacy: {
    title: "Privacy", sub: "개인정보처리방침",
    sections: [
      { heading: "1. 수집 항목", body: "필수항목\n- 이름, 아이디(이메일), 비밀번호, 휴대전화번호\n\n선택항목\n- 생년월일, 성별, 마케팅 수신 동의 여부\n\n소셜 로그인(네이버·카카오) 이용 시\n- 해당 플랫폼에서 제공하는 이름, 이메일, 휴대전화번호, 생년월일, 성별 (이용자 동의 항목에 한함)" },
      { heading: "2. 수집 및 이용 목적", body: "이름, 이메일, 휴대전화번호 → 회원 식별 및 본인 확인\n배송정보 → 상품 배송 및 고객 응대\n구매내역 → 주문 및 결제 처리\n연락처 → 주문·배송 안내 및 고객 상담\n선택정보 → 맞춤형 서비스 제공" },
      { heading: "3. 보유 및 이용기간", body: "회사는 회원 탈퇴 시까지 개인정보를 보유·이용합니다.\n\n다만, 관계 법령에 따라 아래 정보는 일정 기간 보관됩니다.\n- 계약 또는 청약철회 기록: 5년\n- 대금결제 및 재화 공급 기록: 5년\n- 소비자 불만 및 분쟁처리 기록: 3년\n- 접속 로그: 3개월" },
      { heading: "4. 제3자 제공", body: "회사는 이용자의 개인정보를 원칙적으로 외부에 제공하지 않습니다.\n다만, 배송 서비스 제공을 위해 택배사에 이름·연락처·주소를 최소한으로 제공할 수 있으며, 이 경우 이용자에게 사전 고지합니다." },
      { heading: "5. 동의 거부 권리", body: "이용자는 개인정보 수집 및 이용에 대한 동의를 거부할 권리가 있습니다.\n\n다만, 필수항목 수집에 대한 동의를 거부할 경우 회원가입 및 서비스 이용이 제한될 수 있습니다.\n\n문의: hello@underlooked.xyz" },
    ],
  },
};

export const fetchInfoContent = async () => {
  try {
    const { data } = await supabase.from('settings').select('value').eq('key', 'info_content').maybeSingle();
    if (!data?.value) return DEFAULT_INFO_CONTENT;
    return { ...DEFAULT_INFO_CONTENT, ...data.value };
  } catch {
    return DEFAULT_INFO_CONTENT;
  }
};

export const saveInfoContent = async (content) => {
  const { error } = await supabase.from('settings').upsert({ key: 'info_content', value: content });
  if (error) throw error;
  return content;
};

// ─────────────────────────────────────
//  재고 관리
// ─────────────────────────────────────
export const fetchInventory = async (productId) => {
  const { data, error } = await supabase
    .from('inventory')
    .select('*')
    .eq('product_id', productId)
    .order('size').order('color_name');
  if (error) throw error;
  return data || [];
};

export const upsertInventoryRow = async (row) => {
  const { error } = await supabase.from('inventory').upsert({
    product_id: row.product_id,
    size: row.size,
    color_name: row.color_name || '',
    color_id: row.color_id || null,
    quantity: row.quantity,
  }, { onConflict: 'product_id,size,color_name' });
  if (error) throw error;
};

export const deleteInventoryRow = async (id) => {
  const { error } = await supabase.from('inventory').delete().eq('id', id);
  if (error) throw error;
};

// ─────────────────────────────────────
//  적립금
// ─────────────────────────────────────
export const fetchUserPoints = async (userId) => {
  if (!userId) return { total_earned: 0, total_used: 0, available: 0 };
  const { data, error } = await supabase
    .from('user_points')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) return { total_earned: 0, total_used: 0, available: 0 };
  return data || { total_earned: 0, total_used: 0, available: 0 };
};

export const fetchPointTransactions = async (userId) => {
  if (!userId) return [];
  const { data, error } = await supabase
    .from('point_transactions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) return [];
  return data || [];
};

// ─────────────────────────────────────
//  베스트 리뷰 (관리자)
// ─────────────────────────────────────
export const fetchAllReviews = async ({ page = 0, pageSize = 20 } = {}) => {
  const { data, error, count } = await supabase
    .from('reviews')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(page * pageSize, (page + 1) * pageSize - 1);
  if (error) throw error;

  const userIds = [...new Set((data || []).map(r => r.user_id).filter(Boolean))];
  let profileMap = {};
  if (userIds.length) {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, name, username')
      .in('id', userIds);
    (profiles || []).forEach(p => { profileMap[p.id] = p; });
  }

  return {
    reviews: (data || []).map(r => ({ ...r, profiles: profileMap[r.user_id] || null })),
    total: count ?? 0,
  };
};

// review_ids: string[] 배열 또는 단일 string — 둘 다 지원
export const fetchReviewComments = async (reviewIds) => {
  const ids = Array.isArray(reviewIds) ? reviewIds : [reviewIds];
  if (!ids.length) return [];
  const { data, error } = await supabase
    .from('review_comments')
    .select('*')
    .in('review_id', ids)
    .order('created_at');
  if (error) throw error;
  return data || [];
};

export const addReviewComment = async (reviewId, content) => {
  const { error } = await supabase
    .from('review_comments')
    .insert({ review_id: reviewId, content });
  if (error) throw error;
};

export const deleteReviewComment = async (id) => {
  const { error } = await supabase.from('review_comments').delete().eq('id', id);
  if (error) throw error;
};

export const setBestReview = async (reviewId, isBest) => {
  const { error } = await supabase.rpc('set_best_review', {
    p_review_id: reviewId,
    p_is_best: isBest,
  });
  if (error) throw error;
};

// ─────────────────────────────────────
//  적립금 관리 (관리자)
// ─────────────────────────────────────
export const adminGrantPoints = async (userId, amount, description) => {
  const { error } = await supabase.rpc('admin_grant_points', {
    p_user_id: userId,
    p_amount: amount,
    p_description: description,
  });
  if (error) throw error;
};

export const adminDeletePointTransaction = async (txId) => {
  const { error } = await supabase.rpc('admin_delete_point_transaction', {
    p_tx_id: txId,
  });
  if (error) throw error;
};

// ─────────────────────────────────────
//  주문 적립금 사용 (사용자)
// ─────────────────────────────────────
export const spendPointsForOrder = async (orderId, amount) => {
  const { error } = await supabase.rpc('use_points_for_order', {
    p_order_id: orderId,
    p_amount: amount,
  });
  if (error) throw error;
};
